import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { LeadStatus } from '@maximizer/core/shared/domain';
import {
  AbEntryService,
  AppointmentService,
  LeadService,
  OutlookService,
  TrackerService,
  isRecurrenceTruncated,
  mapCalendarAttendeesToAppointmentAttendees,
  prependAttendeesNotInMaximizer,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryType,
  AppointmentPrefill,
  CalendarAttendee,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookAbEntryDetails,
  OutlookCalendarEvent,
  OutlookLeadSearch,
  SaveAppointmentResult,
  SeriesSaveProgressCallback,
} from '@maximizer/outlook/shared/domain';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, from, lastValueFrom, map, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CalendarService {
  private readonly abentryService = inject(AbEntryService);
  private readonly leadService = inject(LeadService);
  private readonly appointmentService = inject(AppointmentService);
  private readonly outlookService = inject(OutlookService);
  private readonly globalStore = inject(GlobalStore);
  private readonly router = inject(Router);
  private readonly tracker = inject(TrackerService);
  private readonly translate = inject(TranslateService);

  async saveAppointment(
    attendees: CalendarAttendee[],
    onProgress?: SeriesSaveProgressCallback,
  ): Promise<SaveAppointmentResult> {
    try {
      const result = await this.createAppointmentFromCalendarEvent(
        attendees,
        onProgress,
      );
      this.trackAppointmentSaved(result, false);
      return result;
    } catch (error) {
      this.trackAppointmentSaveFailed();
      throw error;
    }
  }

  // Emits the same event as saveAppointment(): the review-details form creates
  // the appointment itself, so this is the only hook this service has into it.
  trackAppointmentFormSaved(result: SaveAppointmentResult): void {
    this.trackAppointmentSaved(result, true);
  }

  private async createAppointmentFromCalendarEvent(
    attendees: CalendarAttendee[],
    onProgress?: SeriesSaveProgressCallback,
  ): Promise<SaveAppointmentResult> {
    const currentUserKey = this.globalStore.session()?.user?.key;
    if (!currentUserKey) {
      throw new Error('Save appointment skipped: no current user in session');
    }

    // A failed options read fails the save: without the list, a known location would be stored as free text.
    const { event, fieldOptions } = await lastValueFrom(
      forkJoin({
        event: from(this.outlookService.getCalendarEvent()),
        fieldOptions: this.appointmentService.getFieldOption(),
      }),
    );
    const inMaximizer = attendees.filter((attendee) => attendee.isInMaximizer);

    const appointmentKey = await lastValueFrom(
      this.appointmentService.createFromCalendarEvent(
        this.withAttendeesNotInMaximizer(event, attendees),
        inMaximizer,
        currentUserKey,
        fieldOptions.locations,
        onProgress,
      ),
    );
    if (!appointmentKey) {
      throw new Error('Save appointment failed: no appointment key returned');
    }
    const recurrence = event.recurrence;
    return {
      appointmentKey,
      hasUnsupportedRecurrence: event.hasUnsupportedRecurrence,
      savedThrough:
        recurrence && isRecurrenceTruncated(recurrence)
          ? recurrence.endsOn
          : null,
    };
  }

  private trackAppointmentSaved(
    result: SaveAppointmentResult,
    reviewDetails: boolean,
  ): void {
    this.tracker.trackEvent({
      module: EventModule.Appointment,
      journey: EventJourney.Save,
      action: EventAction.Completed,
      metadata: {
        result: EventMetadata.Success,
        location: this.calendarLocation(),
        reviewDetails,
        truncated: !!result.savedThrough,
        unsupportedRecurrence: result.hasUnsupportedRecurrence,
      },
    });
  }

  private trackAppointmentSaveFailed(): void {
    this.tracker.trackEvent({
      module: EventModule.Appointment,
      journey: EventJourney.Save,
      action: EventAction.Completed,
      metadata: {
        result: EventMetadata.Failed,
        location: this.calendarLocation(),
        reviewDetails: false,
      },
    });
  }

  private calendarLocation(): 'compose' | 'read' {
    return this.outlookService.isCalendarCompose ? 'compose' : 'read';
  }

  async buildAppointmentPrefill(
    attendees: CalendarAttendee[],
  ): Promise<AppointmentPrefill> {
    const event = this.withAttendeesNotInMaximizer(
      await this.outlookService.getCalendarEvent(),
      attendees,
    );

    return {
      subject: event.subject,
      description: event.description,
      startTime: event.start,
      endTime: event.end,
      isAllDay: event.isAllDay,
      isPrivate: event.isPrivate,
      location: event.location,
      attendees: mapCalendarAttendeesToAppointmentAttendees(attendees),
      recurrence: event.recurrence,
      hasUnsupportedRecurrence: event.hasUnsupportedRecurrence,
    };
  }

  private withAttendeesNotInMaximizer(
    event: OutlookCalendarEvent,
    attendees: CalendarAttendee[],
  ): OutlookCalendarEvent {
    return {
      ...event,
      description: prependAttendeesNotInMaximizer(
        event.description,
        attendees,
        this.translate.instant(
          'outlook.calendar.saveAppointment.attendeesNotInMaximizer',
        ),
      ),
    };
  }

  async resolveSavedAppointmentKey(): Promise<string | null> {
    const currentUserKey = this.globalStore.session()?.user?.key;
    if (!currentUserKey) return null;

    const event = await this.outlookService.getCalendarEventSummary();
    const match = await lastValueFrom(
      this.appointmentService.findMatchingAppointment(event, currentUserKey),
    );

    return match?.Key ?? null;
  }

  async enrichAttendees(
    attendees: CalendarAttendee[],
  ): Promise<CalendarAttendee[]> {
    if (!attendees.length) return attendees;

    const canReadLeads = this.globalStore.session()?.user?.rights?.lead?.read;
    const emails = attendees.map((attendee) => attendee.emailAddress);

    return lastValueFrom(
      forkJoin({
        abEntries: this.abentryService
          .getByEmails(emails)
          .pipe(catchError(() => of([] as OutlookAbEntryDetails[]))),
        leads: canReadLeads
          ? this.leadService
              .getByEmails(emails)
              .pipe(catchError(() => of([] as OutlookLeadSearch[])))
          : of([] as OutlookLeadSearch[]),
      }).pipe(
        map(({ abEntries, leads }) =>
          attendees.map((attendee) =>
            this.applyMaximizerMatch(attendee, abEntries, leads),
          ),
        ),
      ),
    );
  }

  navigateToAttendee(attendee: CalendarAttendee): void {
    this.router.navigate([attendee.type, attendee.id]);
  }

  navigateToAddEntry(route: string, attendee: CalendarAttendee): void {
    const cleanedName =
      attendee.emailAddress === attendee.displayName
        ? ''
        : attendee.displayName;
    this.router.navigate([route], {
      queryParams: { email: attendee.emailAddress, name: cleanedName },
    });
  }

  private applyMaximizerMatch(
    attendee: CalendarAttendee,
    abEntries: OutlookAbEntryDetails[],
    leads: OutlookLeadSearch[],
  ): CalendarAttendee {
    const email = attendee.emailAddress.toLowerCase();

    const matchedAbEntry = abEntries.find((abEntry) =>
      abEntry.emails.some(
        (entryEmail) => entryEmail.value.toLowerCase() === email,
      ),
    );
    if (matchedAbEntry) {
      return {
        ...attendee,
        id: matchedAbEntry.key ?? '',
        isInMaximizer: true,
        type: 'abentry' as const,
        abentryType: matchedAbEntry.type.toLowerCase() as AbEntryType,
      };
    }

    const matchedLead = leads.find(
      (lead) =>
        lead.email.toLowerCase() === email &&
        lead.status !== LeadStatus.Converted,
    );
    if (matchedLead) {
      return {
        ...attendee,
        id: matchedLead.key,
        isInMaximizer: true,
        type: 'lead' as const,
      };
    }

    return attendee;
  }
}
